/**
 * useLocalizedTests Hook
 *
 * Provides access to test definitions in the current language.
 * Automatically updates when language changes.
 */

import { useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getTestDefinitions,
  getTestById,
  getTestByCode,
  getTestCategories,
  getTestsByCategory,
} from '../tests';
import { TestDefinition } from '../types';

export function useLocalizedTests() {
  const { language } = useLanguage();

  // Memoized list of all tests
  const tests = useMemo(() => getTestDefinitions(language), [language]);

  // Memoized list of categories
  const categories = useMemo(() => getTestCategories(language), [language]);

  // Get test by ID
  const getTest = useCallback(
    (id: string): TestDefinition | undefined => {
      return getTestById(id, language);
    },
    [language]
  );

  // Get test by code
  const getTestCode = useCallback(
    (code: string): TestDefinition | undefined => {
      return getTestByCode(code, language);
    },
    [language]
  );

  // Get tests by category
  const getCategory = useCallback(
    (category: string): TestDefinition[] => {
      return getTestsByCategory(category, language);
    },
    [language]
  );

  // Search tests by name or description
  const searchTests = useCallback(
    (query: string): TestDefinition[] => {
      const lowerQuery = query.toLowerCase();
      return tests.filter(
        (t) =>
          t.name.toLowerCase().includes(lowerQuery) ||
          t.description?.toLowerCase().includes(lowerQuery) ||
          t.code.toLowerCase().includes(lowerQuery)
      );
    },
    [tests]
  );

  return {
    // Data
    tests,
    categories,
    language,

    // Methods
    getTest,
    getTestCode,
    getCategory,
    searchTests,

    // Counts
    totalTests: tests.length,
    totalCategories: categories.length,
  };
}

export default useLocalizedTests;
